import { z } from "zod";
import {
  actionAvailabilitySchema,
  credentialScopeSchema,
  idSchema,
  instantSchema,
  reasonSchema,
  roleSchema,
} from "@amber/contracts";

export const ROLE_DESCRIPTIONS = {
  viewer: "Read everything they may see, including settings; search; copy links.",
  editor: "Viewer, plus corrections, adding items, profiles, plans, sources, gap actions and Scan now.",
  operator: "Editor, plus retries, reprocessing, source, stage and global pauses, routes and history extensions.",
  admin: "Operator, plus users and roles, budgets, policies and rule values, restricting and deleting.",
} as const;

export const ROLE_LABELS = { viewer: "Viewer", editor: "Editor", operator: "Operator", admin: "Admin" } as const;

export const SIGN_IN_REFUSALS = [
  "invitation_only",
  "wrong_account",
  "deactivated",
  "unverified_email",
  "invitation_expired",
  "failed",
] as const;
export const signInRefusalSchema = z.enum(SIGN_IN_REFUSALS);
export type SignInRefusal = z.infer<typeof signInRefusalSchema>;

export const sessionUserSchema = z.strictObject({
  id: idSchema("usr"),
  display_name: z.string(),
  email: z.email(),
  role: roleSchema,
  domain: z.string(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const sessionResponseSchema = z.strictObject({
  user: sessionUserSchema,
  csrf_token: z.string().min(1),
  admins: z.array(z.strictObject({ display_name: z.string(), email: z.email() })),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

export const signInOptionsSchema = z.strictObject({
  dev_sign_in: z.boolean(),
  google: z.boolean(),
});

export const devSignInRequestSchema = z.strictObject({ role: roleSchema });

const statusDetailSchema = z.strictObject({ at: instantSchema, by: z.string(), reason: z.string() }).nullable();

export const MEMBER_STATUSES = ["active", "invited", "deactivated"] as const;
export const memberStatusSchema = z.enum(MEMBER_STATUSES);

export const userRowSchema = z.strictObject({
  kind: z.literal("user"),
  id: idSchema("usr"),
  display_name: z.string(),
  email: z.email(),
  role: roleSchema,
  status: z.enum(["active", "deactivated"]),
  status_detail: statusDetailSchema,
  last_active_at: instantSchema.nullable(),
  joined_via: z.enum(["invitation", "auto_join_domain"]),
  joined_at: instantSchema,
  revision: z.number().int().positive(),
  actions: z.array(actionAvailabilitySchema),
});
export type UserRow = z.infer<typeof userRowSchema>;

export const invitationRowSchema = z.strictObject({
  kind: z.literal("invitation"),
  id: idSchema("inv"),
  email: z.email(),
  role: roleSchema,
  status: z.literal("invited"),
  expired: z.boolean(),
  expires_at: instantSchema,
  note: z.string().nullable(),
  invited_by: z.string(),
  invited_at: instantSchema,
  link: z.string(),
  actions: z.array(actionAvailabilitySchema),
});
export type InvitationRow = z.infer<typeof invitationRowSchema>;

export const memberRowSchema = z.discriminatedUnion("kind", [userRowSchema, invitationRowSchema]);
export type MemberRow = z.infer<typeof memberRowSchema>;

export const membersQuerySchema = z.strictObject({ status: memberStatusSchema.optional() });

export const lastChangedSchema = z.strictObject({ by: z.string(), at: instantSchema }).nullable();

export const membersResponseSchema = z.strictObject({
  rows: z.array(memberRowSchema),
  counts: z.strictObject({
    all: z.number().int(),
    active: z.number().int(),
    invited: z.number().int(),
    deactivated: z.number().int(),
  }),
  actions: z.array(actionAvailabilitySchema),
  last_changed: lastChangedSchema,
  audit_count: z.number().int().nonnegative(),
});
export type MembersResponse = z.infer<typeof membersResponseSchema>;

export const domainRowSchema = z.strictObject({
  id: idSchema("adm"),
  domain: z.string(),
  default_role: z.literal("viewer"),
  people_joined: z.number().int().nonnegative(),
  added_by: z.string(),
  added_at: instantSchema,
  actions: z.array(actionAvailabilitySchema),
});
export type DomainRow = z.infer<typeof domainRowSchema>;

export const domainsResponseSchema = z.strictObject({
  rows: z.array(domainRowSchema),
  actions: z.array(actionAvailabilitySchema),
});
export type DomainsResponse = z.infer<typeof domainsResponseSchema>;

export const inviteRequestSchema = z.strictObject({
  email: z.email().transform((e) => e.toLowerCase()),
  role: roleSchema,
  note: z.string().trim().max(500).nullable(),
  reason: reasonSchema,
});
export type InviteRequest = z.input<typeof inviteRequestSchema>;

export const inviteResponseSchema = z.strictObject({
  id: idSchema("inv"),
  link: z.string(),
  expires_at: instantSchema,
});

export const changeRoleRequestSchema = z.strictObject({
  role: roleSchema,
  base_revision: z.number().int().positive(),
  reason: reasonSchema,
});
export type ChangeRoleRequest = z.input<typeof changeRoleRequestSchema>;

export const userStateRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  reason: reasonSchema,
});
export type UserStateRequest = z.input<typeof userStateRequestSchema>;

export const reasonOnlyRequestSchema = z.strictObject({ reason: reasonSchema });

export const accessOkResponseSchema = z.object({ ok: z.literal(true) });

export const DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
export const addDomainRequestSchema = z.strictObject({
  domain: z
    .string()
    .trim()
    .toLowerCase()
    .regex(DOMAIN_PATTERN, "Enter a domain such as example.org, without @ or https://"),
  reason: reasonSchema,
});
export type AddDomainRequest = z.input<typeof addDomainRequestSchema>;

export const addDomainResponseSchema = z.object({ id: z.string() });

export const ACCESS_ACTIONS = {
  invite: "access.invite",
  changeRole: "access.change_role",
  deactivate: "access.deactivate",
  reactivate: "access.reactivate",
  revokeInvitation: "access.revoke_invitation",
  copyInvitationLink: "access.copy_invitation_link",
  addDomain: "access.add_domain",
  removeDomain: "access.remove_domain",
  issueCredential: "access.issue_credential",
  revokeCredential: "access.revoke_credential",
} as const;

export const ADMIN_ONLY_REASON = "Only Admins can manage users and roles";

export const ACCESS_AUDIT_LABELS: Record<string, string> = {
  "access.sign_in_refused": "Sign-in refused",
  "access.user_joined": "Joined the workspace",
  "access.action_denied": "Action refused for role",
  "access.invitation_created": "Invited a user",
  "access.invitation_revoked": "Revoked an invitation",
  "access.role_changed": "Changed a role",
  "access.user_deactivated": "Deactivated a user",
  "access.user_reactivated": "Reactivated a user",
  "access.domain_added": "Added an auto-join domain",
  "access.domain_removed": "Removed an auto-join domain",
  "access.credential_issued": "Issued a machine credential",
  "access.credential_revoked": "Revoked a machine credential",
  "access.machine_call": "Machine read",
  "access.machine_call_refused": "Machine call refused",
};

export const ACCESS_SUBJECT_LABELS: Record<string, string> = {
  user: "User",
  invitation: "Invitation",
  auto_join_domain: "Auto-join domain",
  machine_credential: "Machine credential",
};

export const credentialRowSchema = z.strictObject({
  id: idSchema("mcr"),
  name: z.string(),
  scopes: z.array(credentialScopeSchema),
  state: z.enum(["active", "revoked", "expired"]),
  expires_at: instantSchema.nullable(),
  issued_by: z.string(),
  issued_at: instantSchema,
  last_used_at: instantSchema.nullable(),
  revoked: z.strictObject({ at: instantSchema, by: z.string(), reason: z.string() }).nullable(),
  actions: z.array(actionAvailabilitySchema),
});
export type CredentialRow = z.infer<typeof credentialRowSchema>;

export const credentialsResponseSchema = z.strictObject({
  rows: z.array(credentialRowSchema),
  actions: z.array(actionAvailabilitySchema),
});
export type CredentialsResponse = z.infer<typeof credentialsResponseSchema>;

export const issueCredentialRequestSchema = z.strictObject({
  name: z.string().trim().min(1, "Give the credential a name").max(120),
  scopes: z
    .array(credentialScopeSchema)
    .min(1, "Choose at least one scope")
    .refine((scopes) => new Set(scopes).size === scopes.length, "Each scope only once"),
  expires_at: instantSchema.nullable(),
  reason: reasonSchema,
});
export type IssueCredentialRequest = z.input<typeof issueCredentialRequestSchema>;

export const issueCredentialResponseSchema = z.strictObject({
  id: idSchema("mcr"),
  name: z.string(),
  scopes: z.array(credentialScopeSchema),
  expires_at: instantSchema.nullable(),
  secret: z.string().min(1),
});
export type IssueCredentialResponse = z.infer<typeof issueCredentialResponseSchema>;
